import { motion } from "framer-motion";
import { CheckCircle2, Shield, Users2, Hammer } from "lucide-react";

const ABOUT_IMAGE = "https://images.pexels.com/photos/8817853/pexels-photo-8817853.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const FEATURES = [
  "Family-run business with over 30 years experience",
  "All work fully insured",
  "100% satisfaction guaranteed",
  "Quality materials used on every project",
  "Free quotes and consultations",
  "Full project management from start to finish",
];

const ACCREDITATIONS = [
  { name: "Fully Insured", description: "All Work" },
  { name: "100%", description: "Satisfaction Guaranteed" },
  { name: "Quality", description: "Materials" },
];

export const AboutSection = () => {
  return (
    <section
      id="about"
      data-testid="about-section"
      className="py-24 md:py-32 bg-white"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Image Column */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative"
          >
            {/* Main Image */}
            <div className="relative">
              <img
                src={ABOUT_IMAGE}
                alt="Skilled joinery craftsmanship"
                className="w-full aspect-[4/5] object-cover"
              />
              
              {/* Floating Stats Card */}
              <div className="absolute -bottom-8 -right-8 bg-slate-900 text-white p-8 max-w-xs hidden md:block">
                <div className="stat-number text-5xl text-royal-light mb-2">30+</div>
                <div className="font-mono text-xs uppercase tracking-widest text-slate-400">
                  Years of Excellence
                </div>
                <div className="mt-4 pt-4 border-t border-slate-700">
                  <div className="text-sm text-slate-300">
                    Trusted by hundreds of homeowners across Merseyside & Lancashire
                  </div>
                </div>
              </div>
              
              {/* Technical Label */}
              <span className="absolute top-4 left-4 font-mono text-xs text-white/70 uppercase tracking-widest bg-slate-900/50 px-3 py-1">
                ABT-01
              </span>
            </div>
          </motion.div>

          {/* Content Column */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <span className="tech-label text-royal mb-4 block">About Us</span>
            <h2
              data-testid="about-heading"
              className="font-heading text-4xl md:text-5xl font-semibold tracking-tight text-slate-900 mb-6"
            >
              Built on Trust,<br />
              Crafted with Care
            </h2>
            
            <p className="text-lg text-slate-600 leading-relaxed mb-8">
              John Antrobus formed his company over 30 years ago and grew the firm by 
              building a solid reputation as skilled, reliable builders. Since then, John 
              has grown his workforce and allowed his son to develop expert joinery skills, 
              pushing all staff through relevant education and qualifications.
            </p>

            <p className="text-slate-600 leading-relaxed mb-8">
              We specialise in developing residential homes into exactly what the client wants, 
              using our initial consultation to develop a full plan of action—organising 
              tradesmen like electricians, plumbers & plasterers—and managing the work from 
              start to finish.
            </p>

            {/* Features List */}
            <div className="grid sm:grid-cols-2 gap-4 mb-10">
              {FEATURES.map((feature, index) => (
                <div
                  key={index}
                  data-testid={`about-feature-${index}`}
                  className="flex items-start gap-3"
                >
                  <CheckCircle2 size={20} className="text-royal flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-slate-700">{feature}</span>
                </div>
              ))}
            </div>

            {/* Accreditations */}
            <div className="flex flex-wrap gap-4">
              {ACCREDITATIONS.map((acc, index) => (
                <div
                  key={acc.name}
                  data-testid={`accreditation-${index}`}
                  className="badge flex items-center gap-2"
                >
                  <Shield size={14} className="text-royal" />
                  <span className="font-semibold">{acc.name}</span>
                  <span className="text-slate-500">•</span>
                  <span className="text-slate-500">{acc.description}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Values Row - Blue Background */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-24 -mx-6 md:-mx-12 lg:-mx-24 px-6 md:px-12 lg:px-24 py-16 bg-slate-900"
        >
          <div className="grid md:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {[
              {
                icon: Users2,
                title: "Family Values",
                description: "A family-run business that treats every project as if it were our own home.",
              },
              {
                icon: Hammer,
                title: "Expert Craftsmanship",
                description: "Skilled tradesmen with decades of combined experience in building and joinery.",
              },
              {
                icon: Shield,
                title: "Quality Guaranteed",
                description: "All work completed to the highest standards with proper certifications and warranties.",
              },
            ].map((value, index) => (
              <div
                key={value.title}
                data-testid={`value-card-${index}`}
                className="text-center md:text-left"
              >
                <div className="w-14 h-14 flex items-center justify-center bg-slate-800 mb-6 mx-auto md:mx-0">
                  <value.icon size={24} className="text-royal-light" />
                </div>
                <h3 className="font-heading text-xl font-semibold text-white mb-3">
                  {value.title}
                </h3>
                <p className="text-slate-400 text-sm leading-relaxed">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};
